import { useState, useEffect } from "react";
import api, { formatApiError, thumb } from "../lib/api";
import { toast } from "sonner";
import PrivateEventInquiry from "../components/PrivateEventInquiry";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import {
  Star, Sparkles, Crown, Cake, Users, Music, Camera, Gift, Home as HomeIcon,
  GraduationCap, PartyPopper, ArrowRight,
} from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/24407362/pexels-photo-24407362.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const GALLERY = [
  "https://images.pexels.com/photos/24407362/pexels-photo-24407362.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.pexels.com/photos/27807833/pexels-photo-27807833.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.pexels.com/photos/8556164/pexels-photo-8556164.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.pexels.com/photos/33038796/pexels-photo-33038796.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.pexels.com/photos/7180853/pexels-photo-7180853.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.pexels.com/photos/24407354/pexels-photo-24407354.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];
const LIFESTYLE = [
  "https://images.pexels.com/photos/8556164/pexels-photo-8556164.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.pexels.com/photos/33038796/pexels-photo-33038796.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.pexels.com/photos/7180853/pexels-photo-7180853.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];

const PACKAGES = [
  {
    name: "The Mini Society", icon: Cake, accent: "#C6FF33",
    price: "$375",
    tagline: "A fun, low-key party for younger kids or smaller groups. Each guest decorates 4–5 themed cookies and takes them home.",
    points: [
      "1.5 hour private party",
      "4–5 themed cookies per guest",
      "All decorating cookie supplies",
      "Guided cookie decorating activity",
      "Cookie box for each guest",
      "Party host/instructor",
      "Use of studio tools",
      "Setup and cleanup",
    ],
    addl: "$28 each", max: "24 guests",
    note: "Food, drinks, balloons, paper goods, and party favors are not included but may be added separately.",
  },
  {
    name: "The Society Party", icon: Sparkles, accent: "#FF8F80",
    price: "$399",
    tagline: "Our signature party. Each decorator gets their own mini cake to decorate and take home. Choose from a variety of cake design themes before the party, then we'll guide the group through creating their own edible masterpieces.",
    points: [
      "2 hour private party",
      'One 4" cake per guest',
      "Buttercream, sprinkles, simple fondant accents & edible decorations",
      "Guided cake decorating activity",
      "Cake board and cake box for each guest",
      "Party host/instructor",
      "Use of studio tools",
      "Setup and cleanup",
      "Time for singing, photos, and presents if desired",
    ],
    addl: "$35 each", max: "24 guests",
    note: "Food, drinks, balloons, paper goods, party favors, and custom themes are not included but may be added separately.",
  },
  {
    name: "The Signature Society Party", icon: Crown, accent: "#E0218A",
    price: "$595",
    tagline: "Everything in The Society Party, plus extra special touches: a take-home apron, custom birthday details, and a gift for the guest of honor.",
    plusLabel: "Everything in The Society Party, plus:",
    points: [
      "Cake Society apron for each guest to wear and keep",
      'Custom "Happy Birthday, [Name]" welcome sign',
      "Custom birthday child name stickers/decor",
      "Personalized cake box or cake board details",
      "Birthday Creator Kit for the birthday child",
      "Simple photo or cake display moment",
    ],
    addl: "$40 each", max: "24 guests",
    note: "",
  },
];

const OTHER_EVENTS = [
  "Birthday Parties", "Teen Parties", "Adult Birthday Parties", "Bridal Showers",
  "Baby Showers", "Girls' Nights", "Team Building Events", "Family Celebrations",
  "Custom Private Events",
];

const INCLUDED = [
  { icon: HomeIcon, label: "Private Cake Society Studio" },
  { icon: GraduationCap, label: "Guided Instruction" },
  { icon: Gift, label: "All Supplies Included" },
  { icon: Music, label: "Music & Fun Atmosphere" },
  { icon: Camera, label: "Photo Opportunities" },
  { icon: Cake, label: "Take Your Cake Home" },
];

const FAQS = [
  { q: "What ages are parties for?", a: "Everyone! We host parties for kids, teens, and adults. We tailor the guided experience and themes to suit your group's age and skill level." },
  { q: "How many guests can attend?", a: "Our studio comfortably hosts small to medium groups. Let us know your headcount in the inquiry form and we'll confirm the right setup for your celebration." },
  { q: "How long are parties?", a: "Most parties run around two hours of hands-on decorating plus time to celebrate, photograph, and enjoy. We'll finalize timing when we reach out." },
  { q: "Can I bring food or drinks?", a: "In many cases, yes — outside food and drinks can be arranged depending on your package and date. Note your plans in your inquiry and we'll coordinate the details." },
  { q: "What is included?", a: "Use of our private studio, guided instruction, all decorating supplies, a fun music-filled atmosphere, photo moments, and a finished cake for every guest to take home." },
  { q: "How do deposits work?", a: "We hold your date with a deposit once details are confirmed. Our team will walk you through the deposit and balance when we follow up on your inquiry." },
  { q: "Can themes be customized?", a: "Absolutely. Themes, colors, and finishes can be customized — especially with our Deluxe and VIP packages. Share your vision in the special requests field." },
];

const PACKAGE_OPTIONS = ["The Mini Society", "The Society Party", "The Signature Society Party"];
const TIME_OPTIONS = ["10:00 AM–12:00 PM", "2:00–4:00 PM", "5:30–7:30 PM"];
const OCCASION_OPTIONS = ["Birthday", "Team or Group Celebration", "Scout Event", "Bridal Shower", "Baby Shower", "Adult Celebration", "Other"];
const OPTIONAL_ADDONS = [
  { name: "8-inch Matching Celebration Cake", price: "$85", desc: "A coordinating cake designed to match the party project or theme. Serves approximately 20 guests." },
  { name: "Cake Society Favor Bag", price: "$12 per decorating guest", desc: "Includes decorating supplies, a Cake Society sprinkle mix, trendy cake society stickers, a creative challenge card to take home, and 15% off a class or drop-in studio coupon. Final quantity is based on the decorating guest count confirmed the Monday before the party." },
  { name: "Custom Painted Party Banner by Water Lily Creative Co.", price: "$70", desc: "A hand-painted, personalized banner made just for your celebration to make it extra special." },
];
const ADD_ONS = ["Custom painted party banner (Water Lily Creative Co.) — $50", "Food/drinks", "Balloons/photo moment", "Party favors", "Extra time", "Custom theme", "Other"];

function scrollToInquiry() {
  document.getElementById("party-inquiry")?.scrollIntoView({ behavior: "smooth" });
}

const inputClass = "w-full px-4 py-3 border-2 border-[#121212] bg-white focus:outline-none";
const Field = ({ label, children }) => (
  <div><label className="text-xs tracking-[0.2em] uppercase font-bold block mb-2">{label}</label>{children}</div>
);

export default function Parties() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    child_name: "", child_age: "", guest_age_range: "",
    desired_date: "", alternate_date: "", preferred_time: "",
    num_guests: "", occasion: "Birthday", occasion_other: "",
    package: "The Mini Society", special_requests: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [settings, setSettings] = useState({});
  useEffect(() => { api.get("/settings").then(({ data }) => setSettings(data || {})).catch(() => {}); }, []);
  const heroImg = settings.parties_hero || HERO_IMG;
  const gallery = GALLERY.map((d, i) => settings[`parties_gallery_${i + 1}`] || d);
  const banner = LIFESTYLE.map((d, i) => settings[`parties_banner_${i + 1}`] || d);

  const submit = async () => {
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please add your name, email, and phone number");
      return;
    }
    if (!form.preferred_time) {
      toast.error("Please choose a preferred party time");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form, occasion: form.occasion === "Other" ? (form.occasion_other || "Other") : form.occasion };
      delete payload.occasion_other;
      await api.post("/parties/inquiries", payload);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div data-testid="party-inquiry-success" className="pt-40 pb-32 fade-in text-center bg-[#F2E9D8]">
        <div className="max-w-xl mx-auto px-6">
          <PartyPopper className="mx-auto text-[#E0218A]" size={52} />
          <h1 className="font-serif font-black text-5xl text-[#121212] mt-6">Thank you!</h1>
          <p className="mt-4 text-[#121212]/80 text-lg">
            We've received your inquiry and will reach out within 1–2 business days to finalize your celebration.
          </p>
          <button
            data-testid="party-success-back"
            onClick={() => setDone(false)}
            className="mt-10 inline-flex items-center gap-3 px-8 py-4 bg-[#121212] text-[#F2E9D8] hover:bg-black text-xs tracking-[0.25em] uppercase font-bold transition-all"
          >
            Send another inquiry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="parties-page" className="fade-in bg-[#F2E9D8]">
      {/* HERO */}
      <section className="relative pt-32 pb-20 lg:pt-44 lg:pb-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-3 mb-8">
              <span className="h-px w-12 bg-[#E0218A]" />
              <span className="text-xs tracking-[0.3em] uppercase font-bold text-[#E0218A]">Cake Society Parties</span>
            </div>
            <h1 data-testid="parties-title" className="font-serif font-black text-[#121212] leading-[0.95] text-5xl sm:text-6xl lg:text-7xl">
              More Than a Party. <br />
              An <span className="lime-swipe italic">Experience.</span>
            </h1>
            <p className="mt-10 text-lg text-[#121212]/80 max-w-xl leading-relaxed">
              Celebrate with a hands-on cake decorating experience where every guest creates,
              laughs, and leaves with something they're proud of.
            </p>
            <button
              data-testid="hero-book-party"
              onClick={scrollToInquiry}
              className="group mt-12 inline-flex items-center gap-3 px-8 py-4 bg-[#E0218A] text-white hover:bg-[#c41879] text-xs tracking-[0.25em] uppercase font-bold transition-all"
            >
              Book a Party <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] overflow-hidden border-2 border-[#121212]">
              <img src={thumb(heroImg, 940)} alt="Cake Society party" className="w-full h-full object-cover" />
            </div>
            <Star className="absolute -top-4 -left-4 text-[#E0218A] star-rotate" size={48} fill="#E0218A" />
            <div className="absolute -bottom-7 -right-4 bg-[#C6FF33] px-6 py-4 rotate-[-4deg] border-2 border-[#121212]">
              <p className="font-serif italic text-2xl text-[#121212]">create together</p>
              <p className="text-xs tracking-[0.2em] uppercase font-bold">celebrate louder</p>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED PACKAGES */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 py-24 lg:py-28">
        <div className="mb-14">
          <p className="text-xs tracking-[0.3em] uppercase font-bold mb-4">Pick your celebration</p>
          <h2 className="font-serif font-black text-5xl lg:text-6xl text-[#121212] leading-[0.95]">
            Featured party <span className="lime-swipe italic">packages.</span>
          </h2>
          <p data-testid="party-age-note" className="mt-4 text-[#121212]/70 text-base">Cake Society parties are recommended for children ages 6 and up.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {PACKAGES.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={p.name} data-testid={`package-${i}`} className="bg-white border-2 border-[#121212] flex flex-col">
                <div className="h-2" style={{ backgroundColor: p.accent }} />
                <div className="p-8 flex-1 flex flex-col">
                  <div className="w-14 h-14 flex items-center justify-center border-2 border-[#121212]" style={{ backgroundColor: p.accent }}>
                    <Icon size={26} className="text-[#121212]" />
                  </div>
                  <h3 className="font-serif font-black text-3xl text-[#121212] mt-6 leading-tight">{p.name}</h3>
                  <p data-testid={`package-price-${i}`} className="mt-3 font-serif font-black text-3xl text-[#121212]">{p.price}</p>
                  <div className="mt-3 text-sm text-[#121212] space-y-1">
                    <p className="font-bold">10 decorating seats included</p>
                    <p className="text-[#121212]/80">Additional decorating seats: +{p.addl}</p>
                    <p className="text-[#121212]/60 italic">Family and non-decorating guests are welcome to attend.</p>
                  </div>
                  <p className="mt-4 text-[#121212]/75 leading-relaxed">{p.tagline}</p>
                  <ul className="mt-6 space-y-3 flex-1">
                    {p.points.map((pt) => (
                      <li key={pt} className="flex items-start gap-3 text-sm text-[#121212]">
                        <span className="mt-1 shrink-0 w-4 h-4 border-2 border-[#121212]" style={{ backgroundColor: p.accent }} />
                        {pt}
                      </li>
                    ))}
                  </ul>
                  <button
                    data-testid={`package-inquire-${i}`}
                    onClick={() => { setForm((f) => ({ ...f, package: p.name })); scrollToInquiry(); }}
                    className="mt-8 w-full px-6 py-3 bg-[#121212] text-[#F2E9D8] hover:bg-black text-xs tracking-[0.25em] uppercase font-bold transition-all"
                  >
                    Inquire about this
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <div data-testid="party-addon-banner" className="mt-8 border-2 border-[#121212] bg-[#F5EFE6] p-6">
          <div className="flex items-center gap-3 mb-5">
            <span className="shrink-0 w-10 h-10 flex items-center justify-center border-2 border-[#121212] bg-[#C6FF33] font-serif font-black text-lg text-[#121212]">+</span>
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#121212]">Optional add-ons</p>
              <p className="text-sm text-[#121212]/70">Make it extra special — add any of these to your celebration. Just mention them in your inquiry.</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {OPTIONAL_ADDONS.map((a, i) => (
              <div key={i} data-testid={`optional-addon-${i}`} className="bg-white border-2 border-[#121212] p-4 flex flex-col">
                <p className="font-serif font-black text-lg text-[#121212] leading-tight">{a.name}</p>
                <p className="font-serif font-black text-xl text-[#E0218A] mt-1">{a.price}</p>
                <p className="text-sm text-[#121212]/70 mt-2 leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OTHER EVENTS */}
      <section className="bg-[#121212] text-[#F2E9D8] py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
          <p className="text-xs tracking-[0.3em] uppercase font-bold mb-4 text-[#C6FF33]">We host all kinds of gatherings</p>
          <h2 className="font-serif font-black text-4xl lg:text-5xl mb-10">Other events</h2>
          <div className="flex flex-wrap gap-3">
            {OTHER_EVENTS.map((e) => (
              <span key={e} data-testid="other-event" className="px-5 py-3 border-2 border-[#F2E9D8]/30 hover:border-[#C6FF33] hover:text-[#C6FF33] text-sm tracking-[0.1em] uppercase font-semibold transition-all">
                {e}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 py-24 lg:py-28">
        <div className="mb-14 text-center">
          <p className="text-xs tracking-[0.3em] uppercase font-bold mb-4">Every party, always</p>
          <h2 className="font-serif font-black text-5xl lg:text-6xl text-[#121212]">What's <span className="lime-swipe italic">included.</span></h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
          {INCLUDED.map((it) => {
            const Icon = it.icon;
            return (
              <div key={it.label} className="group p-8 bg-white border-2 border-[#121212] hover:bg-[#C6FF33] transition-all flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-full bg-[#F2E9D8] border-2 border-[#121212] flex items-center justify-center group-hover:bg-white transition-all">
                  <Icon size={28} className="text-[#121212]" />
                </div>
                <p className="mt-5 font-serif font-bold text-xl text-[#121212] leading-tight">{it.label}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* PHOTO GALLERY */}
      <section className="bg-[#A89D8A]/15 py-24 lg:py-28 border-y border-[#121212]/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
          <div className="mb-12">
            <p className="text-xs tracking-[0.3em] uppercase font-bold mb-4">From our celebrations</p>
            <h2 className="font-serif font-black text-5xl lg:text-6xl text-[#121212]">Photo <span className="lime-swipe italic">gallery.</span></h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {gallery.map((src, i) => (
              <div key={i} data-testid={`gallery-${i}`} className={`overflow-hidden border-2 border-[#121212] ${i % 5 === 0 ? "md:row-span-2 aspect-[3/4] md:aspect-auto" : "aspect-[4/3]"}`}>
                <img src={thumb(src, 600)} alt={`Party ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 sm:px-12 lg:px-16 py-24 lg:py-28">
        <div className="mb-10">
          <p className="text-xs tracking-[0.3em] uppercase font-bold mb-4">Good to know</p>
          <h2 className="font-serif font-black text-5xl lg:text-6xl text-[#121212]">Questions, <span className="lime-swipe italic">answered.</span></h2>
        </div>
        <Accordion type="single" collapsible className="w-full" data-testid="party-faq">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-b-2 border-[#121212]">
              <AccordionTrigger data-testid={`faq-${i}`} className="text-left font-serif font-bold text-xl text-[#121212] hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#121212]/80 text-base leading-relaxed">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* EMOTIONAL SECTION */}
      <section className="bg-[#121212] text-[#F2E9D8] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
          <div className="text-center max-w-3xl mx-auto">
            <Sparkles className="mx-auto text-[#C6FF33]" size={40} fill="#C6FF33" />
            <h2 className="font-serif font-black text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.0]">
              Every celebration deserves something <span className="text-[#E0218A] italic">unforgettable.</span>
            </h2>
            <p className="mt-6 text-lg text-[#F2E9D8]/75 leading-relaxed">
              Cake Society isn't about making cake — it's about creating memories. The laughter,
              the mess, the pride of finishing something beautiful together. That's the part you keep.
            </p>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-4">
            {banner.map((src, i) => (
              <div key={i} className="overflow-hidden aspect-[4/5] border-2 border-[#F2E9D8]/20">
                <img src={thumb(src, 600)} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INQUIRY FORM */}
      <section id="party-inquiry" className="max-w-3xl mx-auto px-6 sm:px-12 lg:px-16 py-24 lg:py-28 scroll-mt-24">
        <div className="mb-10">
          <p className="text-xs tracking-[0.3em] uppercase font-bold mb-4 text-[#E0218A]">Let's plan it together</p>
          <h2 className="font-serif font-black text-5xl lg:text-6xl text-[#121212] leading-[0.95]">
            Party inquiry.
          </h2>
          <p className="mt-5 text-[#121212]/75">
            Tell us about your celebration and our team will reach out within 1–2 business days to finalize the details.
          </p>
        </div>

        <div className="bg-white border-2 border-[#121212] p-8 space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Name"><input data-testid="party-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} /></Field>
            <Field label="Email"><input data-testid="party-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputClass} /></Field>
            <Field label="Phone number"><input data-testid="party-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputClass} /></Field>
            <Field label="Birthday child's name"><input data-testid="party-child-name" value={form.child_name} onChange={(e) => setForm({ ...form, child_name: e.target.value })} className={inputClass} /></Field>
            <Field label="Birthday child's age"><input data-testid="party-child-age" value={form.child_age} onChange={(e) => setForm({ ...form, child_age: e.target.value })} className={inputClass} /></Field>
            <Field label="Approximate age range of decorating guests (optional)"><input data-testid="party-guest-age-range" value={form.guest_age_range} onChange={(e) => setForm({ ...form, guest_age_range: e.target.value })} placeholder="e.g. 6–9 years" className={inputClass} /></Field>
            <Field label="Desired date"><input data-testid="party-date" type="date" value={form.desired_date} onChange={(e) => setForm({ ...form, desired_date: e.target.value })} className={inputClass} /></Field>
            <Field label="Alternate date (optional)"><input data-testid="party-alt-date" type="date" value={form.alternate_date} onChange={(e) => setForm({ ...form, alternate_date: e.target.value })} className={inputClass} /></Field>
            <Field label="Estimated number of decorating guests"><input data-testid="party-guests" type="number" min={1} value={form.num_guests} onChange={(e) => setForm({ ...form, num_guests: e.target.value })} className={inputClass} /></Field>
            <Field label="Occasion">
              <select data-testid="party-occasion" value={form.occasion} onChange={(e) => setForm({ ...form, occasion: e.target.value })} className={inputClass}>
                {OCCASION_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
          </div>
          {form.occasion === "Other" && (
            <Field label="Tell us about the occasion">
              <input data-testid="party-occasion-other" value={form.occasion_other} onChange={(e) => setForm({ ...form, occasion_other: e.target.value })} placeholder="Describe the occasion" className={inputClass} />
            </Field>
          )}
          <Field label="Preferred party time">
            <div data-testid="party-time-group" className="grid sm:grid-cols-3 gap-3">
              {TIME_OPTIONS.map((t, i) => (
                <label key={t} data-testid={`party-time-${i}`} className={`flex items-center gap-3 px-4 py-3 border-2 cursor-pointer transition-all ${form.preferred_time === t ? "border-[#E0218A] bg-[#E0218A]/5" : "border-[#121212] bg-white hover:bg-[#F2E9D8]"}`}>
                  <input type="radio" name="preferred_time" value={t} checked={form.preferred_time === t} onChange={() => setForm({ ...form, preferred_time: t })} className="w-4 h-4 accent-[#E0218A]" />
                  <span className="text-sm font-bold text-[#121212]">{t}</span>
                </label>
              ))}
            </div>
            <p className="mt-2 text-xs text-[#121212]/60 italic">Party times are subject to availability for the selected date.</p>
          </Field>
          <Field label="Preferred party package">
            <select data-testid="party-package" value={form.package} onChange={(e) => setForm({ ...form, package: e.target.value })} className={inputClass}>
              {PACKAGE_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Special requests">
            <textarea data-testid="party-requests" rows={4} value={form.special_requests} onChange={(e) => setForm({ ...form, special_requests: e.target.value })} className={inputClass + " resize-none"} placeholder="Themes, dietary needs, anything else…" />
          </Field>

          <button
            data-testid="party-submit"
            onClick={submit} disabled={busy}
            className="w-full px-8 py-4 bg-[#E0218A] text-white hover:bg-[#c41879] disabled:opacity-40 text-xs tracking-[0.25em] uppercase font-bold transition-all"
          >
            {busy ? "Sending…" : "Submit Party Inquiry"}
          </button>
          <p className="text-xs text-[#121212]/55 text-center">We never book instantly — every celebration starts with a conversation. A <strong>$100 deposit</strong> reserves your date once details are confirmed and is applied toward your final balance.</p>
        </div>
      </section>

      <PrivateEventInquiry />
    </div>
  );
}
